import React, { useState } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Stack } from '@mui/material';
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import TermsModal from "../components/TermsModal";
import RequirementsModal from "../components/RequirementsModal";
import { useCreateApplicationMutation } from "../components/applicationApiSlice";
import { useTranslation } from "react-i18next"; // Import translations

const theme = createTheme({
  palette: {
    primary: { main: "#2196f3" },
    background: { default: "#808080", paper: "#fff" },
    text: { primary: "#000", secondary: "#000" },
  },
});

const Application = () => {
  const { t } = useTranslation(); // Initialize translation hook
  const navigate = useNavigate();
  const [createApplication, { isLoading, isSuccess, isError, error }] =
    useCreateApplicationMutation();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    line: "",
    facebook: "",
    age: "",
    grade: "",
    reason: "",
    photo: null,
    reportCard: null,
  });

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [requirementsAccepted, setRequirementsAccepted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(true);
  const [requirementsOpen, setRequirementsOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const { name } = e.target;
    const file = e.target.files[0];
    setFormData({ ...formData, [name]: file });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!termsAccepted) {
      alert(t("Application.errors.accept_terms"));
      return;
    }

    if (!requirementsAccepted) {
      alert(t("Application.errors.accept_requirements"));
      return;
    }

    if (!formData.phone) {
      newErrors.phone = t("Application.errors.phone_required");
    }

    if (!formData.email && !formData.line && !formData.facebook) {
      newErrors.contact = t("Application.errors.contact_required");
    }

    if (!formData.name) {
      newErrors.name = t("Application.errors.name_required");
    }

    if (!formData.age) {
      newErrors.age = t("Application.errors.age_required");
    }

    if (!formData.grade) {
      newErrors.grade = t("Application.errors.grade_required");
    }

    if (!formData.reason) {
      newErrors.reason = t("Application.errors.reason_required");
    }

    if (!formData.photo) {
      newErrors.photo = t("Application.errors.photo_required");
    }

    if (!formData.reportCard) {
      newErrors.reportCard = t("Application.errors.reportCard_required");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    try {
      const submissionData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null) {
          submissionData.append(key, value);
        }
      });

      await createApplication(submissionData).unwrap();
      alert(t("Application.success"));
    } catch (err) {
      console.error("Submission failed:", err);
      alert(t("Application.errors.submission_failed"));
    }
  };

  const handleTermsClose = () => setTermsOpen(false);
  const handleTermsOpen = () => setTermsOpen(true);
  const handleRequirementsClose = () => setRequirementsOpen(false);
  const handleRequirementsOpen = () => setRequirementsOpen(true);

  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    handleTermsClose();
    handleRequirementsOpen();
  };

  const handleAcceptRequirements = () => {
    setRequirementsAccepted(true);
    handleRequirementsClose();
  };

  const handleReturnHome = () => {
    navigate("/");
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          backgroundColor: "background.default",
          color: "text.primary",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 2,
          pb: "40px",
          mb: "20px",
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            backgroundColor: "background.paper",
            p: 4,
            borderRadius: 2,
            boxShadow: 3,
            width: "100%",
            maxWidth: 600,
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ color: "text.secondary" }}>
            {t("Application.title")}
          </Typography>

          <TextField
            label={t("Application.fields.name")}
            name="name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            margin="normal"
            variant="outlined"
            error={!!errors.name}
            helperText={errors.name}
          />

          <TextField
            label={t("Application.fields.phone")}
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            fullWidth
            margin="normal"
            variant="outlined"
            error={!!errors.phone}
            helperText={errors.phone}
          />

          <TextField
            label={t("Application.fields.age")}
            name="age"
            type="number"
            value={formData.age}
            onChange={handleChange}
            fullWidth
            margin="normal"
            variant="outlined"
            error={!!errors.age}
            helperText={errors.age}
          />

          <TextField
            select
            label={t("Application.fields.grade")}
            name="grade"
            value={formData.grade}
            onChange={handleChange}
            fullWidth
            margin="normal"
            variant="outlined"
            error={!!errors.grade}
            helperText={errors.grade}
          >
            {["P1", "P2", "P3", "P4", "P5", "P6", "M1", "M2", "M3"].map((grade) => (
              <MenuItem key={grade} value={grade}>
                {t("Application.grades." + grade)}
              </MenuItem>
            ))}
          </TextField>

          <Typography variant="body2" sx={{ mt: 2 }}>
            {t("Application.contact_instruction")}
          </Typography>

          <TextField
            label={t("Application.fields.line")}
            name="line"
            value={formData.line}
            onChange={handleChange}
            fullWidth
            margin="normal"
            variant="outlined"
            error={!!errors.contact}
            helperText={errors.contact}
          />

          <TextField
            label={t("Application.fields.facebook")}
            name="facebook"
            value={formData.facebook}
            onChange={handleChange}
            fullWidth
            margin="normal"
            variant="outlined"
            error={!!errors.contact}
            helperText={errors.contact}
          />

          <TextField
            label={t("Application.fields.email")}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            margin="normal"
            variant="outlined"
            error={!!errors.contact}
            helperText={errors.contact}
          />

          <TextField
            label={t("Application.fields.reason")}
            name="reason"
            value={formData.reason}
            onChange={handleChange}
            fullWidth
            margin="normal"
            variant="outlined"
            multiline
            rows={4}
            error={!!errors.reason}
            helperText={errors.reason}
          />

          <Box display="flex" alignItems="center">
            <Typography variant="body1" sx={{ color: "text.secondary", mr: 1, fontWeight: 'bold' }}>
              {t("Application.fields.upload_photo")}
            </Typography>
          </Box>

          <TextField
            type="file"
            name="photo"
            onChange={handleFileChange}
            fullWidth
            margin="normal"
            variant="outlined"
            inputProps={{
              accept: "image/*", // Restrict file input to image formats
              ...{ capture: "camera" }
            }}
            error={!!errors.photo}
            helperText={errors.photo}
          />

          <Box display="flex" alignItems="center">
            <Typography variant="body1" sx={{ color: "text.secondary", mr: 1, fontWeight: 'bold' }}>
              {t("Application.fields.upload_reportcard")}
            </Typography>
          </Box>

          <TextField
            type="file"
            name="reportCard"
            onChange={handleFileChange}
            fullWidth
            margin="normal"
            variant="outlined"
            inputProps={{
              accept: "image/*", // Restrict file input to image formats
              ...{ capture: "camera" }

            }}
            error={!!errors.reportCard}
            helperText={errors.reportCard}
          />

          <Stack direction="column" spacing={1} alignItems="flex-start"> {/* Use Stack for vertical arrangement */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  name="termsAccepted"
                />
              }
              label={t("Application.fields.accept_terms")}
            />
            <Button onClick={handleTermsOpen} variant="text">
              {t("Application.buttons.read_terms")}
            </Button>

            <FormControlLabel
              control={
                <Checkbox
                  checked={requirementsAccepted}
                  onChange={(e) => setRequirementsAccepted(e.target.checked)}
                  name="requirementsAccepted"
                />
              }
              label={t("Application.fields.accept_requirements")}
            />
            <Button onClick={handleRequirementsOpen} variant="text">
              {t("Application.buttons.read_requirements")}
            </Button>
          </Stack>

          {!isSuccess && (
            <Button type="submit" variant="contained" color="primary" fullWidth disabled={isLoading} sx={{ mt: 2 }}>
              {isLoading ? t("Application.buttons.submitting") : t("Application.buttons.submit")}
            </Button>
          )}

          {isSuccess && (
            <Button onClick={handleReturnHome} variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>
              {t("Application.buttons.home")}
            </Button>
          )}

          {isError && <Typography color="error.main" sx={{ mt: 2 }}>{error.message}</Typography>}
        </Box>
      </Box>

      <TermsModal open={termsOpen} handleClose={handleTermsClose} handleAccept={handleAcceptTerms} />
      <RequirementsModal open={requirementsOpen} handleClose={handleRequirementsClose} handleAccept={handleAcceptRequirements} />
    </ThemeProvider>
  );
};

export default Application;
